import { useEffect, useState, useRef, useCallback, useDebugValue } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import productService from "../../api/services/productService";
import { useUser } from "../../hooks/useUser";
import SafeImage from "../../components/common/SafeImage";

export default function ListOfProducts() {
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const query = useQuery();
  const vendorId = query.get("vendor_id");
  const [statusUpdating, setStatusUpdating] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});
  const perPage = 20;
  const searchInputRef = useRef(null);

  //Get current login user data
  const { data: user } = useUser();

  const fetchProducts = useCallback(
    async (searchTerm = currentSearchQuery) => {
      try {
        setLoading(true);
        let result;
        if (user?.role === "admin" || user?.role === "employee") {
          result = await productService.getAllProducts(
            page,
            perPage,
            searchTerm,
            statusFilter,
          );
        }
        if (result.success) {
          setProducts(Array.isArray(result.data) ? result.data : []);
          setMeta(result.meta || {});
        } else {
          setProducts([]);
          setMeta({});
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch products",
        );
        setProducts([]);
        setMeta({});
      } finally {
        setLoading(false);
      }
    },
    [user?.role, page, perPage, currentSearchQuery, statusFilter],
  );

  useEffect(() => {
    fetchProducts();
  }, [vendorId, page, statusFilter, currentSearchQuery]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (meta.last_page || 1)) {
      setPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    setCurrentSearchQuery(searchQuery);
    setPage(1); // Reset to first page when searching
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1); // Reset to first page when filtering
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const result = await productService.deleteProduct(
          productId,
          user?.role,
        );
        if (result.success) {
          toast.success("Product deleted successfully");
          fetchProducts(); // Refresh the list
        } else {
          // Check for foreign key constraint error in result.message
          if (
            result.message &&
            result.message.includes("SQLSTATE[23000]") &&
            result.message.includes("foreign key constraint fails")
          ) {
            toast.error(
              "Cannot delete this product because it has stock records. Please remove all related stocks before deleting the product.",
            );
          } else {
            toast.error(result.message || "Failed to delete product");
          }
        }
      } catch (error) {
        console.error("Error deleting product:", error);
        const backendMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message;
        if (
          backendMsg &&
          backendMsg.includes("SQLSTATE[23000]") &&
          backendMsg.includes("foreign key constraint fails")
        ) {
          toast.error(
            "Cannot delete this product because it has stock records. Please remove all related stocks before deleting the product.",
          );
        } else {
          toast.error(backendMsg || "Failed to delete product");
        }
      }
    }
  };

  const handleStatusChange = async (product, newStatus) => {
    setStatusUpdating((prev) => ({ ...prev, [product.product_id]: true }));
    try {
      let result;
      if (user?.role === "admin" || user?.role === "employee") {
        result = await productService.updateProductStatus(
          product.product_id,
          newStatus,
          user?.role,
        );
      } else {
        result = await productService.updateProduct(
          product.product_id,
          { status: newStatus },
          user?.role,
        );
      }
      if (result.success) {
        setProducts((prev) =>
          prev.map((p) =>
            p.product_id === product.product_id
              ? { ...p, status: newStatus }
              : p,
          ),
        );
        toast.success("Status updated!");
      } else {
        toast.error(result.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [product.product_id]: false }));
    }
  };

  const isAdminOrEmployee = user?.role === "admin" || user?.role === "employee";

  if (loading) {
    return (
      <>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-sm-12">
          <div className="title-header option-title d-sm-flex d-block justify-content-between align-items-center mb-4">
            <h2>Products List</h2>
            <div className="d-flex align-items-center">
              <input
                ref={searchInputRef}
                type="text"
                className="form-control form-control-sm me-2"
                style={{ width: 220 }}
                placeholder="Search by product name..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
              />
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <i className="fas fa-search me-1"></i>
                )}
                Search
              </button>
              <select
                className="form-select form-select-sm w-auto me-2"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ul className="mb-0">
                <li>
                  <Link
                    className="btn btn-solid"
                    to={"/adminproductsaddorupdate/"}
                  >
                    Add Product
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div>
            <div
              className="table-responsive"
              style={{
                maxWidth: "100%",
                overflowX: "hidden",
                overflow: "scroll",
              }}
            >
              <table
                className="table all-package theme-table table-product"
                id="table_id"
                style={{ width: "100%", marginBottom: 0 }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "10%", padding: "6px 4px" }}>
                      Product ID
                    </th>
                    <th style={{ width: "10%", padding: "6px 4px" }}>Image</th>
                    <th style={{ width: "20%", padding: "6px 4px" }}>
                      Product Name
                    </th>
                    <th style={{ width: "15%", padding: "6px 4px" }}>
                      Category
                    </th>
                    <th style={{ width: "10%", padding: "6px 4px" }}>
                      Subcategories
                    </th>
                    <th style={{ width: "10%", padding: "6px 4px" }}>Status</th>
                    <th style={{ width: "15%", padding: "6px 4px" }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {products.map((product) => (
                    <tr key={product.product_id}>
                      <td
                        style={{
                          maxWidth: "0",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.product_id}
                      </td>
                      <td>
                        <div
                          className="table-image"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <SafeImage
                            src={(() => {
                              try {
                                return JSON.parse(product.product_image)?.[0];
                              } catch (e) {
                                return product.product_image;
                              }
                            })()}
                            className="img-fluid"
                            alt={product.product_name}
                            style={{
                              objectFit: "cover",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        </div>
                      </td>

                      <td
                        style={{
                          maxWidth: "0",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.product_name}
                      </td>

                      <td
                        style={{
                          maxWidth: "0",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.category_name}
                      </td>

                      <td>{product.subcategory_name}</td>

                      <td className="status-close text-center">
                        {user?.role === "admin" || user?.role === "employee" ? (
                          <>
                            <select
                              className={`form-select form-select-sm w-auto ${
                                product.status === "active"
                                  ? "border-success"
                                  : "border-danger"
                              }`}
                              value={product.status}
                              onChange={(e) =>
                                handleStatusChange(product, e.target.value)
                              }
                              disabled={!!statusUpdating[product.product_id]}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                            {statusUpdating[product.product_id] && (
                              <span
                                className="spinner-border spinner-border-sm ms-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            )}
                          </>
                        ) : (
                          <span>{product.status}</span>
                        )}
                      </td>

                      <td style={{ padding: "4px" }}>
                        <ul
                          className="list-inline mb-0 d-flex align-items-center"
                          style={{ gap: "4px" }}
                        >
                          <li className="list-inline-item">
                            <Link
                              to={`/productsdetails/${product.product_id}`}
                              className="btn btn-sm btn-light p-1"
                              style={{ lineHeight: 1, minWidth: 0 }}
                            >
                              <i
                                className="ri-eye-line"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              ></i>
                            </Link>
                          </li>
                          <li className="list-inline-item">
                            <Link
                              to={`/adminproductsaddorupdate/${product.product_id}`}
                              className="btn btn-sm btn-light p-1"
                              style={{ lineHeight: 1, minWidth: 0 }}
                            >
                              <i
                                className="ri-pencil-line"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              ></i>
                            </Link>
                          </li>
                          <li className="list-inline-item">
                            <button
                              onClick={() => handleDelete(product.product_id)}
                              className="btn btn-sm btn-light p-1"
                              style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                lineHeight: 1,
                                minWidth: 0,
                              }}
                            >
                              <i
                                className="ri-delete-bin-line"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                  color: "#dc3545",
                                  verticalAlign: "middle",
                                }}
                              ></i>
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No products found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {(meta.total > 0 || Object.keys(meta).length > 0) && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <p className="mb-0">
                {meta.total > 0
                  ? `Showing ${meta.from || 1} to ${
                      meta.to || products.length
                    } of ${meta.total} entries`
                  : `Page ${page} of ${meta.last_page || 1}`}
              </p>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      &laquo; Prev
                    </button>
                  </li>
                  {Array.from(
                    { length: Math.max(1, meta.last_page || 1) },
                    (_, i) => i + 1,
                  ).map((pg) => (
                    <li
                      key={pg}
                      className={`page-item${pg === page ? " active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pg)}
                      >
                        {pg}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item${
                      page === Math.max(1, meta.last_page || 1)
                        ? " disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === Math.max(1, meta.last_page || 1)}
                    >
                      Next &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
